import { useCallback, useEffect, useState } from 'react';
import { skipToken, useQuery, type QueryKey } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { billing } from '@/services/billing';
import { useAbortableTaxLookup } from './use-abortable-tax-lookup';
import { hasTaxableAddress, isDebouncedTaxField, TAX_LOOKUP_DEBOUNCE_MS } from '../utils/tax-lookup-rules';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';
import { sleep } from '@/utils/sleep';

/**
 * The address fields that can move the tax rate, per vendor. The lookup request is not the same shape
 * for both vendors, so the trigger set cannot be one flat list:
 *
 * - Ordway asks Avalara with `{ country, zip_code, region: state, address1 }`. The backend reads exactly
 *   those four on this branch and ignores `state` / `city` / `address_line1`
 *   (`services/ui_api/billing/tax_rate.go:81`), so for an Ordway account the **street is a rate input**.
 * - Legacy Stripe asks TaxJar with `{ country, zip_code, state }` (`tax_rate.go:46`), where `region` and
 *   `address1` are ignored, so the street cannot move the rate.
 *
 * A single flat list would therefore be wrong for one vendor or the other: it would either re-price an
 * account whose rate cannot move, or leave an Ordway customer who corrected the street on the previous
 * street's tax.
 */
const STRIPE_TAX_FIELDS: ReadonlySet<keyof CreditCardFormAddress> = new Set(['country', 'state', 'zipCode']);
const ORDWAY_TAX_FIELDS: ReadonlySet<keyof CreditCardFormAddress> = new Set([
  'country',
  'state',
  'zipCode',
  'addressLine1',
]);

/**
 * The lookup input: the four address fields a tax-rate request is made of, plus whether the change that
 * asked for it was typed (so the request waits out the debounce first). This is the only state the hook
 * keeps — keying the query on it makes the query itself the source of truth for "a lookup is in flight",
 * so there is no separate pending flag to keep in step.
 *
 * Two changes that agree on all four fields produce the same key and are deduped, which is what we want:
 * re-selecting a value that is already set should not restart a lookup that is already running for it.
 */
type TaxLookupTarget = {
  country: string;
  state: string;
  zipCode: string;
  addressLine1: string;
  isTyped: boolean;
};

export type TaxRateRefresh = {
  /** Refreshes the tax rate after an address field changed. A non-tax field is ignored. */
  onAddressChange: (address: CreditCardFormAddress, changedField: keyof CreditCardFormAddress) => void;
  /**
   * Whether a lookup (or its debounce) is in flight. Submit is blocked while it is true, so
   * "Pay now" can never charge against a total computed for the previous address.
   */
  isPending: boolean;
};

/**
 * Refreshes the billing tax rate as the billing address changes, and reports whether a refresh is in
 * flight: only the fields the account's vendor actually keys the lookup on trigger a request, typed
 * fields are debounced, an address we cannot price clears the stored rate right away, and a newer change
 * supersedes an older one.
 *
 * The lookup is a GET, so the request lives in a `useQuery` keyed on the address it is for, and
 * `isFetching` is the pending flag. The debounce runs inside the query function rather than before it,
 * which is what lets `isFetching` also cover the "still typing, nothing sent yet" window — submit is
 * blocked from the keystroke, not just from the request.
 *
 * No error is exposed because there is none to expose: `updateTaxRate` reports a failed lookup to Sentry
 * and clears the rate itself, on purpose — a tax lookup that fails must not block checkout.
 */
/** One place for the key shape, so the abort site removes exactly the entry the run is registered under. */
function taxRateQueryKey(target: TaxLookupTarget | null): QueryKey {
  return ['checkoutTaxRate', target];
}

export function useTaxRateRefresh(): TaxRateRefresh {
  const isOrdwayCustomer = useStore(billing.isOrdwayCustomer);
  const [target, setTarget] = useState<TaxLookupTarget | null>(null);
  // Supersedes the previous lookup. React Query cancels neither a query function whose key has changed
  // nor one whose query has gone dormant, and `updateTaxRate` writes the rate into the billing store
  // rather than returning it into the query cache — so the query layer has nothing to undo a stale write
  // with, and the signal is the only thing that can stop it. Without this, selecting a non-US country
  // (which clears the target, so that change takes the reset branch) let the in-flight US lookup land
  // afterwards and price a foreign address with US tax while submit was open.
  // Also drops the superseded run's cache entry, so an address returning to it starts a fresh lookup
  // instead of being deduped onto a run that can no longer write a rate. See `useAbortableTaxLookup`.
  const lookup = useAbortableTaxLookup();

  // Leaving the form must not let a late response write a rate for an address nobody is editing, and
  // must not leave the rate it already wrote behind either. Nothing clears it again on the way back in:
  // the rate is seeded from the billing profile on the legacy Stripe path only, and a lookup runs only
  // from an address edit — so on the Ordway path the summary would price the next visit's blank or
  // prefilled address with this visit's tax, with submit open. Clearing here is what keeps the hook's
  // invariant true: a rate is reported only for the address on screen.
  useEffect(
    () => () => {
      lookup.abort();
      billing.resetTaxRate();
    },
    [lookup],
  );

  const query = useQuery({
    queryKey: taxRateQueryKey(target),
    queryFn:
      target == null
        ? skipToken
        : async () => {
            // Abort from in here rather than from the change handler: a change that resolves to the key
            // already in flight never reaches this point (React Query dedupes it), and it must not have
            // the lookup it is waiting on cancelled out from under it.
            const signal = lookup.begin(taxRateQueryKey(target));

            if (target.isTyped) {
              await sleep(TAX_LOOKUP_DEBOUNCE_MS, signal);
              if (signal.aborted) {
                return null;
              }
            }

            await billing.updateTaxRate.perform({
              country: target.country,
              state: target.state,
              zipCode: target.zipCode,
              addressLine1: target.addressLine1,
              signal,
            });
            // The totals read the rate from the billing store; this is returned only so the query has a
            // settled value of its own.
            return billing.taxRate.get()?.rate ?? null;
          },
    // A rate belongs to one address and one edit: never serve it from cache, and never keep it once the
    // address has moved on.
    staleTime: 0,
    gcTime: 0,
  });

  const onAddressChange = useCallback(
    (address: CreditCardFormAddress, changedField: keyof CreditCardFormAddress) => {
      const taxFields = isOrdwayCustomer ? ORDWAY_TAX_FIELDS : STRIPE_TAX_FIELDS;
      if (!taxFields.has(changedField)) {
        return;
      }

      if (!hasTaxableAddress(address)) {
        // No query function runs on this path, so the in-flight lookup has to be aborted here or it
        // writes its rate back over this reset.
        lookup.abort();
        setTarget(null);
        billing.resetTaxRate();
        return;
      }

      setTarget({
        country: address.country,
        state: address.state,
        zipCode: address.zipCode,
        addressLine1: address.addressLine1,
        isTyped: isDebouncedTaxField(changedField),
      });
    },
    [isOrdwayCustomer, lookup],
  );

  return { onAddressChange, isPending: query.isFetching };
}
